import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { SettingsService, SettingType, NewSettingRequest } from 'src/app/core/settings/settings.service';
import { EnvironmentService } from 'src/app/core/environment/environment.service';
import { SessionService } from 'src/app/auth/session/session.service';
import { diffSettingTypes, SettingTypeFieldDiff } from '../setting-type-diff.util';

// A diff row (see diffSettingTypes) plus the verification checkbox state the modal tracks per field.
interface FieldVerification extends SettingTypeFieldDiff {
  verified: boolean;
}

@Component({
  selector: 'dcc-promote-setting-modal',
  templateUrl: './promote-setting-modal.component.html',
  styleUrls: ['./promote-setting-modal.component.scss']
})
export class PromoteSettingModalComponent implements OnInit {
  @Input() settingToPromote!: SettingType;
  // 'promote' (default): a requester submits a change to the higher environment.
  // 'review': a second SettingTypeAdmin approves a change already awaiting approval in prod.
  @Input() mode: 'promote' | 'review' = 'promote';
  // Set in review mode — the id of the pending change to approve.
  @Input() requestId?: string;
  // Set in review mode — the setting's current definition in the target environment, so the modal
  // can show approvers a diff of what the change alters. Undefined for a create (nothing exists yet).
  @Input() previousSetting?: SettingType;

  verificationForm!: FormGroup;
  isPromoting = false;
  fields: FieldVerification[] = [];
  targetEnvironment: string = '';

  constructor(
    private activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private settingsService: SettingsService,
    private environmentService: EnvironmentService,
    private sessionService: SessionService,
    private toastr: ToastrService
  ) {}

  get isReview(): boolean {
    return this.mode === 'review';
  }

  // In review mode, whether the target already has this setting (an update, so a diff is meaningful)
  // or not (a create — every field is new). previousSetting is only supplied in review mode.
  get showDiff(): boolean {
    return this.isReview && !!this.previousSetting;
  }

  get changedFieldCount(): number {
    return this.fields.filter(f => f.changed).length;
  }

  get title(): string {
    return this.isReview
      ? `Approve promotion to ${this.targetEnvironment}`
      : `Promote Setting to ${this.targetEnvironment}`;
  }

  get confirmLabel(): string {
    return this.isReview ? `Approve for ${this.targetEnvironment}` : `Promote to ${this.targetEnvironment}`;
  }

  get inProgressLabel(): string {
    return this.isReview ? 'Approving...' : 'Promoting...';
  }

  ngOnInit(): void {
    const higherEnv = this.environmentService.getHigherEnvironment();
    this.targetEnvironment = higherEnv 
      ? this.environmentService.getEnvironmentDisplayName(higherEnv)
      : '';

    this.initializeVerificationFields();
    this.verificationForm = this.fb.group({
      verifications: this.fb.array(
        this.fields.map(() => this.fb.control(false))
      )
    });
  }

  get verifications(): FormArray {
    return this.verificationForm.get('verifications') as FormArray;
  }

  get allFieldsVerified(): boolean {
    return this.verifications.controls.every(control => control.value === true);
  }

  private initializeVerificationFields(): void {
    // The field set + diff logic is shared with the audit grid (see setting-type-diff.util); the modal
    // layers a per-field verification checkbox on top of each diff row.
    this.fields = diffSettingTypes(this.settingToPromote, this.previousSetting)
      .map(field => ({ ...field, verified: false }));
  }

  async onPromote(): Promise<void> {
    if (!this.allFieldsVerified) {
      this.toastr.warning(`Please verify all fields before ${this.isReview ? 'approving' : 'promoting'}.`);
      return;
    }

    this.isPromoting = true;
    try {
      if (this.isReview) {
        await this.approve();
      } else {
        await this.promote();
      }
      this.activeModal.close(true);
    } catch (error) {
      const verb = this.isReview ? 'approve' : 'promote';
      console.error(`Error trying to ${verb} setting:`, error);
      this.toastr.error(`Failed to ${verb} setting to ${this.targetEnvironment}. Please try again.`);
    } finally {
      this.isPromoting = false;
    }
  }

  private async promote(): Promise<void> {
    const result = await this.settingsService.promoteSetting(this.toDefinition());
    if (result?.requiresApproval) {
      this.toastr.success(
        `Setting "${this.settingToPromote.name}" was submitted to ${this.targetEnvironment} and is awaiting approval by another Setting Type Admin.`
      );
    } else {
      this.toastr.success(`Successfully promoted setting "${this.settingToPromote.name}" to ${this.targetEnvironment}`);
    }
  }

  private async approve(): Promise<void> {
    const higherEnvConfig = this.environmentService.getHigherEnvironmentConfig();
    if (!higherEnvConfig) {
      throw new Error('No higher environment available for approval');
    }

    await this.settingsService.reviewSettingTypeChange(higherEnvConfig.settingsUpdate, this.requestId, {
      approved: true,
      reviewer: this.sessionService.getCurrentSession()?.name ?? 'unknown',
      reviewerEmail: this.sessionService.getCurrentSession()?.email ?? 'unknown',
      description: `Approved promotion of "${this.settingToPromote.name}" to ${this.targetEnvironment}`,
    });
    this.toastr.success(`Approved promotion of "${this.settingToPromote.name}" to ${this.targetEnvironment}`);
  }

  private toDefinition(): NewSettingRequest {
    return {
      name: this.settingToPromote.name,
      label: this.settingToPromote.label,
      category: this.settingToPromote.category,
      class: (this.settingToPromote as any)?.['class'] || 'Free',
      defaultValue: this.settingToPromote.defaultValue,
      dataType: this.settingToPromote.dataType as any,
      allowableValues: this.settingToPromote.allowableValues || [],
      minValue: this.settingToPromote.minValue || '',
      maxValue: this.settingToPromote.maxValue || '',
      units: this.settingToPromote.units || '',
      isLegacy: this.settingToPromote.isLegacy || false,
      isExchangeable: (this.settingToPromote as any)?.['isExchangeable'] !== undefined
        ? (this.settingToPromote as any)['isExchangeable']
        : true,
      applicableHardwarePlatforms: this.settingToPromote.applicableHardwarePlatforms
    };
  }

  onCancel(): void {
    this.activeModal.dismiss();
  }
}
